import ScoreUtils from "../utils/ScoreUtils.js";
import StorageService from "../services/StorageService.js";
import SoundManager from "../utils/SoundManager.js";
import AccountSync from "../services/AccountSync.js";
import * as FlagPreloader from "../utils/FlagPreloader.js";
import { pickQuestion } from "../utils/QuestionWarmup.js";
import { renderAccountNudge, NUDGE_MIN_SCORE } from "../utils/AccountNudge.js";
import { sanitizeRun } from "../services/syncMerge.js";
import { saveWeekRun } from "../utils/WeekRun.js";
import * as Passport from "../utils/Passport.js";
import * as ReviewDeck from "../utils/ReviewDeck.js";
import { zoneCountries } from "../utils/ZoneScope.js";
import { recordLabPlay, renderLabRating } from "../utils/LabFeedback.js";
import { formatDuration } from "../utils/TimeUtils.js";
import { renderErrorsRecap, endGameAnchor } from "../utils/ErrorsRecap.js";
import { enterEndGame } from "../utils/EndGameUI.js";
import { showConfirmation } from "../utils/ConfirmationPopup.js";

/**
 * Base class for all game modes (Classic, Hard) for both Flag and Capital Nerd.
 * Centralizes lifecycle, lives management, progress tracking and sharing.
 */
export default class BaseGame {
    /**
     * @param {string} gameId Identifier (ex: 'flagnerd', 'capitalnerdhard')
     * @param {string} pageSelector CSS selector for the page
     * @param {object} [config] Sharing / end-game metadata:
     *   { gameNameKey, modeLabelKey, mode, shareEmoji, slug }. Drives the generic
     *   `initShare()` and `renderEndGameStats()`; omit to override those per-game.
     */
    constructor(gameId, pageSelector, config = {}) {
        this.gameId = gameId;
        this.page = document.querySelector(pageSelector);
        if (!this.page) return;

        this.config = config;

        // Common UI elements
        this.flagContainer = this.page.querySelector(".flag-container");
        this.answerContainer = this.page.querySelector(".answer-container");
        this.winMessage = this.page.querySelector(".win-message");
        this.looseMessage = this.page.querySelector(".loose-message");
        this.progressContainer = this.page.querySelector(".progress-container");
        this.titleContainer = this.page.querySelector(".title-container");

        // Hearts
        this.life1 = this.page.querySelector(".heart-1");
        this.life2 = this.page.querySelector(".heart-2");
        this.life3 = this.page.querySelector(".heart-3");

        // State management
        const savedLifes = StorageService.load(`${this.gameId}.lifes`);
        this.lifes = savedLifes !== null ? parseInt(savedLifes) : 3;

        // Auto-fix for game-over state to allow replay
        if (this.lifes <= 0) {
            this.lifes = 3;
            StorageService.remove(`${this.gameId}.countriesleft`);
            StorageService.remove(`${this.gameId}.current`);
            StorageService.remove(`${this.gameId}.proposals`);
            StorageService.remove(`${this.gameId}.startedAt`);
            StorageService.remove(`${this.gameId}.errors`);
            StorageService.save(`${this.gameId}.lifes`, 3);
        }

        this.canGuess = true;
        // Restaurées avec la partie : sans ça un simple F5 en cours de route effaçait
        // les fautes déjà commises, et le bestRun enregistré à l'arrivée s'affichait
        // « sans faute » au classement alors que des vies avaient bien été perdues.
        const savedErrors = StorageService.load(`${this.gameId}.errors`);
        this.errors = Array.isArray(savedErrors) ? savedErrors : [];
    }

    /**
     * Common initialization steps.
     */
    async init() {
        this.initCountries();
        this.startClock();
        // Fermer « Comment jouer ? » avant la première réponse remet le chrono à
        // zéro : le temps de lecture de la règle ne compte pas.
        document.addEventListener("geonerd:gameinfo-closed", () => this.restartClockIfUnplayed());
        this.newProposal();
        this.reloadButton();
        this.bindRestartButton();
        this.initShare();
        this.updateHeartsUI();
        this.updateProgress();
    }

    /**
     * Persisted once at the true start and reused as-is across reloads/resumes, so the
     * clock can never be reset to look artificially fast — it can only ever run longer.
     */
    startClock() {
        const savedStartedAt = StorageService.load(`${this.gameId}.startedAt`);
        if (savedStartedAt) {
            this.gameStartedAt = savedStartedAt;
        } else {
            this.gameStartedAt = Date.now();
            StorageService.save(`${this.gameId}.startedAt`, this.gameStartedAt);
        }
        this.startElapsedTicker();
    }

    restartClockIfUnplayed() {
        const untouched = this.allCountriesCount === this.countriesLeft.length && this.errors.length === 0 && this.lifes === 3;
        if (!untouched) return;
        this.gameStartedAt = Date.now();
        StorageService.save(`${this.gameId}.startedAt`, this.gameStartedAt);
    }

    /**
     * Secondes écoulées depuis le vrai début de la partie (reprises comprises).
     *
     * Tronquées, jamais arrondies : un chronomètre affiche 42 tant que la 43e
     * seconde n'est pas écoulée. Avec un arrondi, le compteur affichait encore
     * « 0:42 » à 42,6 s alors que le récap, calculé au même instant, montrait
     * 0:43 — une seconde apparaissait de nulle part à la fin de la partie.
     */
    elapsedSeconds() {
        return Math.max(0, Math.floor((Date.now() - this.gameStartedAt) / 1000));
    }

    /**
     * Affiche le chrono de la partie en cours, à côté du score.
     *
     * Le temps était déjà mesuré, persisté et départageait les ex aequo du
     * classement, mais restait invisible : le joueur courait sans le savoir. Le
     * compteur est injecté ici plutôt que dans les gabarits — il est purement
     * dynamique, et cela évite de l'oublier sur l'une des sept pages concernées.
     */
    startElapsedTicker() {
        const progress = this.page.querySelector(".progress");
        if (!progress || this.elapsedTimer) return;

        this.elapsedEl = progress.querySelector(".elapsed");
        if (!this.elapsedEl) {
            progress.insertAdjacentHTML("beforeend", `<span class="elapsed"><span class="elapsed-icon" aria-hidden="true"></span><span class="elapsed-value"></span></span>`);
            this.elapsedEl = progress.querySelector(".elapsed");
        }

        const tick = () => {
            const value = this.elapsedEl.querySelector(".elapsed-value");
            if (value) value.textContent = formatDuration(this.elapsedSeconds());
        };
        tick();
        this.elapsedTimer = setInterval(tick, 1000);
    }

    /** Fige le chrono affiché : la partie est finie, il ne doit plus avancer. */
    stopElapsedTicker() {
        if (!this.elapsedTimer) return;
        clearInterval(this.elapsedTimer);
        this.elapsedTimer = null;
    }

    /**
     * Handle countries initialization and persistence.
     * @param {Array} defaultList List of countries to use if nothing in storage
     */
    initCountries(defaultList = zoneCountries(geoNerdApp.countries, this.zone)) {
        const saved = StorageService.load(`${this.gameId}.countriesleft`);
        this.allCountriesCount = defaultList.length;
        // Pool persisté vide = partie déjà gagnée avant ce chargement. `newProposal()`
        // va enchaîner directement sur `finishGame()` : le run ne doit alors être ni
        // recompté ni rechronométré (cf. `recordWin()`).
        this.alreadyFinishedOnLoad = Array.isArray(saved) && saved.length === 0;

        if (saved) {
            this.countriesLeft = saved;
            // Sync names with current language
            this.countriesLeft = this.countriesLeft.map(stored => {
                const current = defaultList.find(c => c.code === stored.code);
                return current ? { ...stored, name: current.name, capital: current.capital } : stored;
            });
        } else {
            this.countriesLeft = [...defaultList];
        }

    }

    /**
     * Affiche une question une fois ses drapeaux en cache, avec le loader en
     * attendant. Rien ne bloque plus sur les 193 drapeaux : seul celui de la
     * question compte (voir FlagPreloader). Le jeton écarte un rendu tardif si
     * une autre question a été tirée entre-temps (rejouer, recommencer).
     */
    whenFlagsReady(codes, render, { clearAnswers = false } = {}) {
        const token = this.questionToken = (this.questionToken || 0) + 1;
        if (!codes.length || !FlagPreloader.isActive() || FlagPreloader.areLoaded(codes)) {
            render();
            return;
        }
        if (this.flagContainer) this.flagContainer.innerHTML = '<div class="flag-loader"></div>';
        if (clearAnswers && this.answerContainer) this.answerContainer.innerHTML = "";
        FlagPreloader.load(codes).then(() => {
            if (token === this.questionToken) render();
        });
    }

    /**
     * Sets up the next country to guess. 
     * Children must implement UI part if needed.
     */
    newProposal() {
        if (this.countriesLeft.length === 0) {
            this.finishGame();
            return;
        }

        const current = StorageService.load(`${this.gameId}.current`);
        if (!current || current.found) {
            this.rightAnswer = this.drawQuestion();
            StorageService.save(`${this.gameId}.current`, this.rightAnswer);
        } else {
            this.rightAnswer = current;
        }
    }

    /**
     * Prochain pays à faire deviner : les premières questions d'une partie sont
     * tirées parmi les pays les plus connus du pool (voir QuestionWarmup).
     */
    drawQuestion() {
        const asked = (this.allCountriesCount - this.countriesLeft.length) + this.errors.length;
        return pickQuestion(this.countriesLeft, asked);
    }

    /**
     * Save bestRun if this run beats the stored one (score → errors → duration),
     * and the week's best run (weekly leaderboard). Called at the end of a run,
     * won or lost.
     */
    _saveBestRun(score, errors) {
        // Même source que le compteur affiché et que le récap : trois calculs
        // séparés du même temps finissaient par ne plus dire la même chose.
        const duration = this.finalDuration ?? this.elapsedSeconds();
        // Ceinture et bretelles : si le chrono est malgré tout incohérent (durée nulle
        // ou impossible au regard du score), il est neutralisé à 59:59 plutôt que de
        // partir tel quel au classement, où il battrait tout le monde. Même règle que
        // le merge de synchro, des deux côtés.
        // `recordedAt` date le record : il permet d'afficher sa fraîcheur au classement
        // et distingue un run d'aujourd'hui d'un run hérité (les anciens n'en ont pas).
        const candidate = sanitizeRun({ score, errors, duration, recordedAt: Date.now() });
        saveWeekRun(this.gameId, candidate);
        const stored = StorageService.load(`${this.gameId}.bestRun`);
        const existing = sanitizeRun(stored);
        // Un record déjà corrompu par l'ancien bug est réparé sur place, sans attendre
        // la synchro : sinon il resterait imbattable localement (durée 0).
        if (existing && stored && existing.duration !== stored.duration) {
            StorageService.save(`${this.gameId}.bestRun`, existing);
        }
        if (!existing) {
            StorageService.save(`${this.gameId}.bestRun`, candidate);
            return;
        }
        const a = candidate, b = existing;
        if (a.score !== b.score) { if (a.score > b.score) StorageService.save(`${this.gameId}.bestRun`, candidate); return; }
        const ea = a.errors ?? Infinity, eb = b.errors ?? Infinity;
        if (ea !== eb) { if (ea < eb) StorageService.save(`${this.gameId}.bestRun`, candidate); return; }
        const da = a.duration ?? Infinity, db = b.duration ?? Infinity;
        if (da < db) StorageService.save(`${this.gameId}.bestRun`, candidate);
    }

    /**
     * Persist stats/bestRun for a completed game. Split out of `finishGame()` so
     * games that defer the recap screen (Map Nerd, behind a "view recap" button)
     * can still record the result the instant the game actually ends, independently
     * of when the player chooses to see it.
     *
     * `countriesleft: []` stays in storage after a win, so a reload/back on the win
     * screen re-runs newProposal() → finishGame(). Guard so stats/bestRun are only
     * ever recorded once per completion — an unguarded rerun would look like a fresh
     * 0-error run, chronometered from the page load (i.e. 0 s), and would overwrite
     * a legitimate bestRun with an unbeatable one.
     *
     * Two guards, not one: the `finished` flag, and the fact that the pool was already
     * empty when the page loaded. The flag alone was blind to the runs completed before
     * it existed — their `countriesleft: []` was still in storage, so the next visit to
     * the page re-recorded them from scratch.
     */
    recordWin() {
        this.stopElapsedTicker();
        if (StorageService.load(`${this.gameId}.finished`) || this.alreadyFinishedOnLoad) {
            StorageService.save(`${this.gameId}.finished`, true);
            return;
        }
        StorageService.save(`${this.gameId}.finished`, true);
        // Le chrono et le record précédent sont figés ici, pas au moment d'afficher
        // le récap : les jeux carte le montrent derrière un bouton, parfois bien
        // après la dernière réponse.
        this.finalDuration = this.elapsedSeconds();
        this.previousBest = parseInt(StorageService.load(`${this.gameId}.best`, 0)) || 0;
        this.isNewBest = this.allCountriesCount > this.previousBest;
        this._saveBestRun(this.allCountriesCount, this.errors.length);
        StorageService.save(`${this.gameId}.best`, this.allCountriesCount);
        this.saveStats(this.allCountriesCount);
    }

    /**
     * Display the win recap. Split from `recordWin()` for the same reason.
     */
    showWinMessage() {
        enterEndGame(this.page);
        this.renderEndGameStats(this.winMessage);
        // Gagner avec deux fautes ne disait pas lesquelles : le récap n'existait
        // qu'à la défaite, alors que c'est là qu'on apprend.
        this.renderErrors(this.winMessage);
        this.winMessage.classList.add("show");
        if (this.progressContainer) this.progressContainer.style.display = "none";
        if (this.titleContainer) this.titleContainer.style.display = "none";
    }

    /**
     * Common win logic.
     */
    finishGame() {
        SoundManager.victory();
        if (this.flagContainer) this.flagContainer.remove();
        if (this.answerContainer) this.answerContainer.remove();
        this.page.querySelector(".btn-restart-ingame")?.remove();
        this.recordWin();
        this.showWinMessage();
    }

    /**
     * Immediately deduct a life and persist it — call synchronously on wrong answer
     * so a page reload can't undo the penalty before the animation completes.
     *
     * Le cœur se brise dans la foulée. Il ne changeait qu'à la fin du tour, plus
     * d'une seconde après la faute : la sanction arrivait détachée de son geste,
     * alors que les jeux carte la montraient tout de suite.
     */
    penalizeLife() {
        this.lifes--;
        StorageService.save(`${this.gameId}.lifes`, this.lifes);
        this.updateHeartsUI();
    }

    /**
     * Handle life management and game over.
     */
    updateLife(decrease) {
        if (decrease) {
            this.lifes--;
            StorageService.save(`${this.gameId}.lifes`, this.lifes);
        }

        this.updateHeartsUI();

        if (this.lifes <= 0) {
            SoundManager.gameOver();
            this.lifes = 0;
            StorageService.save(`${this.gameId}.lifes`, 0);

            this.recordLoss();
            enterEndGame(this.page);
            this.renderEndGameStats(this.looseMessage);
            this.renderErrors(this.looseMessage);

            gsap.to([this.flagContainer, this.answerContainer], {
                opacity: 0,
                duration: 0.4,
                onComplete: () => {
                    if (this.flagContainer) this.flagContainer.remove();
                    if (this.answerContainer) this.answerContainer.remove();
                    this.page.querySelector(".btn-restart-ingame")?.remove();
                    this.looseMessage.classList.add("show");
                    if (this.progressContainer) this.progressContainer.style.display = "none";
                    if (this.titleContainer) this.titleContainer.style.display = "none";
                }
            });
        }
        this.updateProgress();
    }

    /**
     * Clôt une partie perdue : chrono arrêté et figé, record précédent retenu,
     * score et stats enregistrés.
     *
     * Extrait d'`updateLife()` parce que les jeux carte la redéfinissent (mode
     * Infini, récap différé derrière un bouton) : sans point commun, le chrono
     * continuait de tourner sous leur écran de fin, et le temps envoyé au
     * classement était celui du clic sur « Voir le récapitulatif », pas celui de
     * la dernière réponse.
     */
    recordLoss() {
        this.stopElapsedTicker();
        const currentBest = parseInt(StorageService.load(`${this.gameId}.best`, 0)) || 0;
        const currentScore = this.allCountriesCount - this.countriesLeft.length;

        this.finalDuration = this.elapsedSeconds();
        this.previousBest = currentBest;
        this.isNewBest = currentScore > currentBest;

        if (currentScore > currentBest) {
            StorageService.save(`${this.gameId}.best`, currentScore.toString());
        }
        this._saveBestRun(currentScore, this.errors.length);
        this.saveStats(currentScore);
    }

    /**
     * Hearts UI update.
     */
    updateHeartsUI() {
        if (!this.life1 || !this.life2 || !this.life3) return;

        this.life1.classList.toggle("loose", this.lifes < 1);
        this.life2.classList.toggle("loose", this.lifes < 2);
        this.life3.classList.toggle("loose", this.lifes < 3);
    }

    /**
     * Wipes the current run's state and reloads the page — abandons it for a
     * fresh one without recording a loss or touching stats. Shared by the
     * end-of-game "Rejouer" buttons and any in-game restart button.
     */
    restartGame() {
        StorageService.remove(`${this.gameId}.countriesleft`);
        StorageService.remove(`${this.gameId}.current`);
        StorageService.remove(`${this.gameId}.proposals`);
        StorageService.remove(`${this.gameId}.lifes`);
        StorageService.remove(`${this.gameId}.finished`);
        StorageService.remove(`${this.gameId}.startedAt`);
        StorageService.remove(`${this.gameId}.errors`);
        window.location.reload();
    }

    /**
     * Reload logic.
     */
    reloadButton() {
        this.page.querySelectorAll(".reload").forEach(button => {
            button.addEventListener("click", () => this.restartGame());
        });
    }

    /**
     * Wires an in-game restart button (`.btn-restart-ingame`), separate from
     * the end-of-run `.reload` buttons: it discards a run still in progress,
     * so it asks for confirmation first instead of firing immediately.
     */
    bindRestartButton() {
        const button = this.page.querySelector(".btn-restart-ingame");
        if (!button) return;
        button.addEventListener("click", () => {
            const lang = geoNerdApp.languageManager;
            showConfirmation(
                lang.t("settings.confirmTitle"),
                lang.t("settings.confirmMessage"),
                () => this.restartGame()
            );
        });
    }

    /**
     * Progress bar update.
     */
    updateProgress() {
        const found = this.allCountriesCount - this.countriesLeft.length;
        const best = StorageService.load(`${this.gameId}.best`, 0);

        const foundEl = this.page.querySelector(".progress .found");
        const bestEl = this.page.querySelector(".progress .best .value");

        if (foundEl) foundEl.textContent = found.toString();
        if (bestEl) bestEl.textContent = best.toString();
    }

    /**
     * Stats saving.
     */
    saveStats(score) {
        ScoreUtils.saveStats(this.gameId, score);
        // Modes du Lab : la partie est comptée pour le back-office.
        if (this.config.labMode) recordLabPlay(this.config.labMode);
        // Partie terminée → pousse la sauvegarde en ligne immédiatement (pas de debounce :
        // le lien "classement" affiché juste après est cliquable avant qu'un push différé n'ait lieu).
        AccountSync.push();
    }

    /**
     * Persist the remaining-countries pool.
     */
    updateStorage() {
        StorageService.save(`${this.gameId}.countriesleft`, this.countriesLeft);
    }

    /**
     * Mark the current answer as found: drop it from the pool and persist.
     */
    markFound() {
        // Passeport : un drapeau reconnu ou une capitale trouvée laisse un tampon.
        if (this.config.passportStamp) Passport.stamp(this.rightAnswer.code, this.config.passportStamp);
        this.countriesLeft = this.countriesLeft.filter(elem => elem.code !== this.rightAnswer.code);
        StorageService.save(`${this.gameId}.current`, { found: true });
        this.updateStorage();
    }

    /**
     * Record a wrong answer: log the error, clear the current question/proposals
     * and deduct a life synchronously (anti-reload-cheat).
     * @param {object} error Error entry consumed by `renderErrorItem`.
     */
    markWrong(error) {
        // Révision espacée : la faute revient plus tard dans /revision.
        if (this.config.passportStamp) ReviewDeck.recordMiss(this.config.passportStamp, this.rightAnswer.code);
        this.recordError(error);
        StorageService.remove(`${this.gameId}.current`);
        StorageService.remove(`${this.gameId}.proposals`);
        this.penalizeLife();
    }

    /**
     * Log a mistake and persist it right away, alongside the life it costs — the
     * error list survives a reload exactly like the lives counter does, so the
     * recap and the `errors` field of the bestRun stay truthful.
     * @param {object} error Error entry consumed by `renderErrorItem`.
     */
    recordError(error) {
        this.errors.push(error);
        StorageService.save(`${this.gameId}.errors`, this.errors);
    }

    /**
     * Fade out the question, resolve life state, then load the next proposal.
     * @param {Function} [reset] Optional cleanup run before life resolution (e.g. clearing an input).
     */
    advanceTurn(reset) {
        setTimeout(() => {
            gsap.to([this.flagContainer, this.answerContainer], {
                opacity: 0,
                duration: 0.4,
                onComplete: () => {
                    if (reset) reset();
                    this.updateLife(false);
                    if (this.lifes > 0) {
                        this.flagContainer.style.opacity = "1";
                        this.answerContainer.style.opacity = "1";
                        this.newProposal();
                    }
                }
            });
        }, 700);
    }

    /**
     * End game stats rendering, driven by `this.config`. Override only for games
     * that need a different denominator or layout.
     */
    renderEndGameStats(container) {
        if (!this.config.modeLabelKey) return;
        const score = this.allCountriesCount - this.countriesLeft.length;
        ScoreUtils.renderEndGameResults(
            container,
            this.gameId,
            this.endGameModeLabel(),
            score,
            this.allCountriesCount,
            this.config.mode,
            {
                duration: this.finalDuration ?? this.elapsedSeconds(),
                best: parseInt(StorageService.load(`${this.gameId}.best`, 0)) || 0,
                isNewBest: this.isNewBest
            }
        );
        if (this.isNewBest && score >= NUDGE_MIN_SCORE) renderAccountNudge(container, "record");
        else container.querySelector(".account-nudge")?.remove();
        // Modes du Lab : « Tu as aimé ce mode ? », juste avant les boutons.
        if (this.config.labMode) renderLabRating(container, this.config.labMode, endGameAnchor(container));
    }

    /**
     * Label shown in the end-game score card. Combines the game name and the
     * mode (e.g. "Flag Nerd · Classic") so the recap stays self-explanatory
     * even with the question title hidden.
     */
    endGameModeLabel() {
        const lang = geoNerdApp.languageManager;
        const mode = lang.t(this.config.modeLabelKey) + (this.config.zoneKey ? ` · ${lang.t(this.config.zoneKey)}` : "");
        return this.config.gameNameKey ? `${lang.t(this.config.gameNameKey)} · ${mode}` : mode;
    }

    renderErrors(container) {
        const lang = geoNerdApp.languageManager;
        renderErrorsRecap(container, this.errors, err => this.renderErrorItem(err), lang.t("game.yourMistakes"));
    }

    renderErrorItem(error) {
        return "";
    }

    /**
     * Cœurs en texte brut (❤️/💔) pour le score partagé : le partage part au
     * presse-papiers vers d'autres apps, donc pas les SVG `currentColor` de
     * l'UI (voir Pictos.js) — de vrais emoji, comme le reste du texte partagé.
     * `💔` pour zéro vie plutôt qu'une chaîne vide : sans ça le score d'une
     * défaite (toujours à 0 vie) laisserait un "· ·" vide entre les segments.
     */
    heartsText(lifes) {
        return lifes > 0 ? "❤️".repeat(lifes) : "💔";
    }

    /**
     * Sharing logic, driven by `this.config`. The shared score is
     * `found/total · vies restantes · temps`, les mêmes informations que la
     * ligne du classement pour ces modes (voir `renderStatRow` dans
     * PlayerProfile.js) — sans ça un score seul ne disait pas s'il venait
     * d'une victoire nette ou d'une partie perdue sur la dernière vie.
     * Override only for games with a different score format.
     */
    initShare() {
        if (!this.config.slug) return;
        const { gameNameKey, modeLabelKey, shareEmoji, slug } = this.config;
        this.page.querySelectorAll(".btn-share").forEach(btn => {
            btn.addEventListener("click", () => {
                const lang = geoNerdApp.languageManager;
                const score = this.allCountriesCount - this.countriesLeft.length;
                const duration = this.finalDuration ?? this.elapsedSeconds();
                const scoreText = `${score}/${this.allCountriesCount} · ${this.heartsText(this.lifes)} · ${formatDuration(duration)}`;
                geoNerdApp.shareResult(
                    btn,
                    lang.t(gameNameKey),
                    lang.t(modeLabelKey),
                    scoreText,
                    shareEmoji,
                    slug
                );
            });
        });
    }
}
